import { useFormSchema } from "@portal/providers/FormSchemaProvider";
import { FieldValues, useWatch } from "react-hook-form";
import {
	isEmpty,
	isFieldPartial,
	isFieldRequiredForApproval,
	isFieldRequiredForSubmit,
} from "../../utils/schema-fields";
import { Text } from "../primitives";
import { Tooltip } from "../ui/Tooltip";
import { FormField } from "./FormField";
import { FormItemLabel } from "./FormItemLabel";
import { FormComponentProps } from "./types";

/**
 * Vertical form layout: renders a label above the input with partial/required-for-approval indicators.
 * Wraps a field input (passed as children) with a FormItemLabel and optional tooltip.
 */
export const FormComponent = <TFieldValues extends FieldValues>({
	formMethods,
	id,
	name,
	label,
	subLabel,
	tooltip,
	children,
	inline = false,
	className,
	inputClassName,
	labelClassName,
	schema,
	partialRequiresSubmit = true,
	required: requiredProp,
}: FormComponentProps<TFieldValues> & { children: React.ReactNode }) => {
	const schemaFromContext = useFormSchema();
	const effectiveSchema = schema ?? schemaFromContext;
	const fieldValue = useWatch({ control: formMethods.control, name });
	const { isSubmitted } = formMethods.formState;

	const shouldCheck = partialRequiresSubmit ? isSubmitted : true;
	const partial =
		shouldCheck &&
		!!effectiveSchema &&
		isFieldPartial(effectiveSchema, name) &&
		isEmpty(fieldValue);
	const requiredForApproval =
		shouldCheck &&
		!!effectiveSchema &&
		isFieldRequiredForApproval(effectiveSchema, name) &&
		isEmpty(fieldValue);
	const required =
		requiredProp ?? (!!effectiveSchema && isFieldRequiredForSubmit(effectiveSchema, name));

	return (
		<FormField id={id || name} condensed={inline} className={className}>
			<FormItemLabel
				partial={partial}
				requiredForApproval={requiredForApproval}
				subLabel={subLabel}
				condensed={inline}
				className={labelClassName}
			>
				<span>
					{label}
					{required && <Text color="danger">{"\u00A0"}*</Text>}
				</span>
				{tooltip && (
					<Tooltip
						id={`tooltip-${name}`}
						content={tooltip}
						className="ml-[5px] text-[16px]"
						condensed={inline}
					/>
				)}
			</FormItemLabel>
			{inputClassName ? <div className={inputClassName}>{children}</div> : children}
		</FormField>
	);
};
